import { Link } from "react-router-dom";
import { categories } from "../content";
import { getThumbnailPath } from "../shared/getThumbnailPath";
import { useEffect } from "react";

const GalleryIndex = () => {
  // Создаем структурированные данные для галереи
  const galleryStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Галерея фотолокаций Лесной Усадьбы",
    description:
      "Выберите интересующую вас категорию для просмотра всех фотографий. Каждая локация имеет свое уникальное настроение и атмосферу.",
    url: "https://fotodacha.ru/#/gallery",
    hasPart: categories.map((category) => ({
      "@type": "ImageGallery",
      name: category.title,
      description: category.description,
      url: `https://fotodacha.ru/#/gallery/${category.id}`,
      image: category.preview,
    })),
  };

  // Создаем структурированные данные для хлебных крошек
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Главная",
        item: "https://fotodacha.ru/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Галерея фотолокаций",
        item: "https://fotodacha.ru/#/gallery",
      },
    ],
  };

  // Устанавливаем метатеги для SEO
  useEffect(() => {
    // Устанавливаем заголовок страницы
    document.title =
      "Галерея фотолокаций в Солнечногорском районе | 8 уникальных фотозон в стиле фармхаус";

    // Устанавливаем мета-описание
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Выберите одну из 8 уникальных фотозон в Солнечногорском районе Подмосковья: дом, сад, поляна, пикник, костер, мольберт и другие локации для идеальной фотосессии."
      );
    }

    // Устанавливаем canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", "https://fotodacha.ru/#/gallery");

    // Добавляем структурированные данные для галереи
    let galleryScriptTag = document.querySelector("#gallery-structured-data");
    if (!galleryScriptTag) {
      galleryScriptTag = document.createElement("script");
      galleryScriptTag.id = "gallery-structured-data";
      galleryScriptTag.type = "application/ld+json";
      document.head.appendChild(galleryScriptTag);
    }
    galleryScriptTag.textContent = JSON.stringify(galleryStructuredData);

    // Добавляем структурированные данные для хлебных крошек
    let breadcrumbScriptTag = document.querySelector(
      "#breadcrumb-structured-data"
    );
    if (!breadcrumbScriptTag) {
      breadcrumbScriptTag = document.createElement("script");
      breadcrumbScriptTag.id = "breadcrumb-structured-data";
      breadcrumbScriptTag.type = "application/ld+json";
      document.head.appendChild(breadcrumbScriptTag);
    }
    breadcrumbScriptTag.textContent = JSON.stringify(breadcrumbStructuredData);

    // Очистка при размонтировании
    return () => {
      if (galleryScriptTag) {
        document.head.removeChild(galleryScriptTag);
      }
      if (breadcrumbScriptTag) {
        document.head.removeChild(breadcrumbScriptTag);
      }
    };
  }, []);

  return (
    <div className="section bg-linen">
      <div className="container">
        {/* Хлебные крошки для навигации и SEO */}
        <nav aria-label="Хлебные крошки" className="breadcrumbs py-2 mb-4">
          <ol className="flex flex-wrap items-center text-sm text-gray-600">
            <li className="breadcrumb-item">
              <Link to="/" className="hover:text-terracotta transition-colors">
                Главная
              </Link>
              <span className="mx-2">/</span>
            </li>
            <li className="breadcrumb-item" aria-current="page">
              <span className="text-terracotta font-medium">
                Галерея фотолокаций
              </span>
            </li>
          </ol>
        </nav>

        <h1 className="text-3xl font-bold text-center font-serif header-color md:text-4xl mb-4">
          Галерея фотолокаций
        </h1>

        <p className="text-center text-gray-700 max-w-3xl mx-auto mb-12">
          Выберите интересующую вас категорию для просмотра всех фотографий.
          Каждая локация имеет свое уникальное настроение и атмосферу.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/gallery/${category.id}`}
              className="category-card-link"
              aria-label={`Просмотреть фотозону ${category.title}`}
            >
              <div className="category-card h-80 overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="relative w-full h-full">
                  <img
                    src={getThumbnailPath(category.preview, "lg")}
                    alt={`Фотозона ${category.title} в Солнечногорском районе Подмосковья`}
                    className="w-full h-full object-cover transition-transform hover:scale-105 duration-700"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-6">
                    <h3 className="text-xl font-bold text-white font-serif mb-2">
                      {category.title}
                    </h3>
                    <p className="text-sm text-white/80">
                      {category.description}
                    </p>
                    <span className="mt-4 inline-block px-4 py-2 bg-terracotta/90 text-white text-sm rounded-full">
                      Смотреть все фото
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* <div className="mt-12 text-center">
          <Link to="/#booking" className="btn btn-primary">
            Забронировать фотосессию
          </Link>
        </div> */}
      </div>
    </div>
  );
};

export default GalleryIndex;
